import React, { useEffect, useRef, useState } from "react";
import { Box, Divider, Typography, Tooltip } from "@mui/material";
import { MRT_ColumnDef } from "material-react-table";
import ProductInfo from "./product-details/product-info";
import ProductFacilitiesInfo from "./product-details/product-facilities-info";
import ProductFirstProcessorInfo from "./product-details/product-first-processor-info";
import AdditionalInfo from "./product-details/additional-info";
import RawMaterialInfo from "./product-details/raw-material-info";
import { StyledButton } from "../../../../../../shared/components/buttons/custom-button";
import CustomTable from "../../../../../../shared/components/table/custom-table";
import { ActionsMenu } from "../../../../../../shared/components/table/custom-actions";
import ActionsModalPopup, {
  ActionButtonConfig,
} from "../../../../../../shared/components/modals/action-popup";
import CustomStepper from "../../../../../../shared/components/stepper/steppers";
import {
  IAdditionalInfo,
  ICertifiedProductDetails,
  ITCProduct,
  ITcProductCertifiedWeightInformation,
  ITcProductFacilitiesInformation,
  ITcProductFirstProcessorInformation,
  ITcProductRawMaterialGeographicInformation,
  ITcProductRawMaterialInformation,
  mapToPayload,
  normalizeStringArray,
  payloadToMap,
} from "../../../tc-models/tc-req-dto/tc-certified-product-details.req_dto";
import {
  FloatingButtonContainer,
  InnerButton,
} from "../../../../../../shared/components/buttons/floating-button-container";
import { transactionCertificateService } from "../../../tc-service/transaction-certificate.service";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import useNotification from "../../../../../../shared/hooks/useNotification";
import { useFormActions } from "../../../../../../shared/hooks/form-builder.hooks";
import { useApi } from "../../../../../../shared/hooks/useApi";
import { LoaderComponent } from "../../../../../../shared/components/loaders/page-loader";
import {
  DELETECONFIG,
  TC_CREATE_ROUTE_CONFIG,
  TCAppConfig,
} from "../../../../../../shared/app-configs/tc-app-configs";
import AlertPopup from "../../../../../../shared/components/modals/alert-moadal";
import _ from "lodash";
import { useTransactionCertificateStore } from "../../../../../../store/transaction-certificates.store";
import { Utills } from "../../../../../../shared/app-configs/utilit";
import RenderDetailPanel from "./render-detail-panel";
import {
  getShipmentIdsByNumbers,
  getShipmentIdsByProductId,
  mapProductData,
  mapProductDataToStepData,
  ProductShipmentResponse,
} from "./certified-products.model";
import { ApiShipmentResponseItem } from "../shipments/shipments.model";
import { fetchProductaddDetails } from "./product-api";
import { dtrackItService } from "../../../tc-service/dtrackit.service";
import ProductCertifiedWeight from "./product-details/product-certified-weight";
import ProductGeoInfo from "./product-details/product-geo-info";

// ===== INTERFACES =====
interface CertifiedProductsComponentsProps {
  onNextTab: () => Promise<void>;
}

interface ProductFormData {
  productInfo: ITCProduct;
  geographicInfo: ITcProductRawMaterialGeographicInformation[];
  additionalInfo: IAdditionalInfo;
  rawMaterialInfo: ITcProductRawMaterialInformation[];
  productCertifiedWeightInfo: ITcProductCertifiedWeightInformation[];
  productFacilitiesInfo: ITcProductFacilitiesInformation[];
  productFirstProcessorInfo: ITcProductFirstProcessorInformation[];
}

// ===== CONSTANTS =====
const MAX_PRODUCTS_ALLOWED = 100;
const PRODUCT_LIMIT_ERROR =
  "Maximum of 100 products allowed per TC. Please remove some products before adding new ones.";

// ===== MAIN COMPONENT =====
const CertifiedProductsComponents: React.FC<
  CertifiedProductsComponentsProps
> = ({ onNextTab }) => {
  // ===== STATE MANAGEMENT ====
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [editingIndex, setEditingIndex] = useState<number>(-1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [apiShipmentsData, setAPIData] = useState<any[]>([]);
  const [data, setData] = useState<any[]>([]);
  const [pendingSave, setPendingSave] = useState<any | null>(null);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [productId, setProductId] = useState<string | null>(null);
  const [shipmentId, setShipmentId] = useState<string | null>(null);
  const [shipmentNo, setShipmentNo] = useState<string | null>(null);
  const [originalProductArticleNo, setOriginalProductArticleNo] =
    useState<string>("");
  const [originalShipmentIds, setOriginalShipmentIds] = useState<string[]>([]);

  // UI state
  const [tcProductArticleNo, setTcProductArticleNo] = useState<string>("");
  const [popUpMessage, setPopUpMessage] = useState<string>("");
  const [popUpTitle, setPopUpTitle] = useState<string>("");
  const [popUpBtnName, setPopUpBtnName] = useState<string>("");
  const [isNextEnabled, setNextEnabled] = useState<boolean>(false);
  const [disableOnEdit, setDisableOnEdit] = useState<boolean>(false);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [isCopy, setIsCopy] = useState<boolean>(false);
  const [isRawMaterialValid, setIsRawMaterialValid] = useState<boolean>(false);

  // Data state
  const [shipmentData, setShipmentData] = useState<ApiShipmentResponseItem[]>(
    []
  );
  const [tableData, setTableData] = useState<ProductShipmentResponse[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const [tcStandardsCache, setTcStandardsCache] = useState<string[]>([]);
  const [certifiedRawMaterialCodes, setCertifiedRawMaterialCodes] = useState<any[]>([]);
  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(true);

  // ===== HOOKS =====
  const navigate = useNavigate();
  const { TE_ID, formtype } = useParams<string>();
  const hasCreateTc: boolean = formtype === "create-tc";
  const hasSubmitTc: boolean = formtype === "submited-tc";
  const hasRejectedTc: boolean = formtype === "rejected-tc";
  const {
    loading,
    error,
    executeGet,
    setLoadingState,
    executePost,
    executeDelete,
  } = useApi();
  const { loading: nextLoader, executeGet: executeShipment } = useApi();
  const { notify } = useNotification();
  const { isRestrictPopUpSave, setRestrictPopUpSave, getTCStandardGOTS } =
    useTransactionCertificateStore();
  const {
    getCurrentFormValues,
    getCurrentTableData,
    updateTableData,
    replaceTableDatas,
    updateTableRowbyIndex,
    removeTableRowbyIndex,
    updateField,
    setDeleteSave,
    setEditSave,
    resetFormValueByPage,
    getDeleteSave,
  } = useFormActions("certified-products");

  // ===== INITIAL DATA =====
  const initTcProduct: ITCProduct = {
    tcProductOrderNo: [],
    tcProductArticleNo: "",
    tcProductProductionDate: "",
    tcProductShipmentDocNo: [],
    tcProductNumberOfUnits: "",
    tcProductUnitsMetric: "",
    tcProductNetShippingWeight: 0,
    tcProductSupplementaryWeight: 0,
    tcProductInfoSupplementaryWeight: "",
    tcProductCategoryCode: "",
    tcProductCategoryUserSpecificTerm: "",
    tcProductDetailCode: "",
    tcProductDetailUserSpecificTerm: "",
    tcProductCategoryCodeDescription: "",
    tcProductDetailCodeDescription: "",
  };

  const initAdditonalInfo: IAdditionalInfo = {
    tcProductInfoAdditional: "",
    tcProductInfoFabricWeightAndConstruct: "",
    tcProductInfoFabricWeightOfFinishedProduct: "",
    tcProductInfoFiberLength: "",
    tcProductInfoYarnCount: "",
    tcProductAdditives: "",
    tcProductAdditivesWeight: 0,
    tcProductFeedstock: [],
    tcProductPercentTextileWaste: "",
    tcProductPackagingFor: [],
    tcProductGtin: "",
    tcProductReclaimedMaterialSource: [],
    tcProductInfoTechnicalSpecifications: "",
    tcProductOriginFacilityTeId: [],
  };

  const [stepData, setStepData] = useState<ProductFormData>({
    productInfo: initTcProduct,
    geographicInfo: [],
    additionalInfo: initAdditonalInfo,
    rawMaterialInfo: [],
    productCertifiedWeightInfo: [],
    productFacilitiesInfo: [],
    productFirstProcessorInfo: [],
  });

  // ===== REFS =====
  const productInfoRef = useRef<any>(null);
  const additionalInfoRef = useRef<any>(null);
  const rawMaterialInfoRef = useRef<any>(null);
  const geographicInfoRef = useRef<any>(null);
  const productCertifiedWeightInfoRef = useRef<any>(null);
  const productFacilitiesInfoRef = useRef<any>(null);
  const productFirstProcessorInfoRef = useRef<any>(null);

  const formRefs = [
    productInfoRef,
    additionalInfoRef,
    rawMaterialInfoRef,
    geographicInfoRef,
    productCertifiedWeightInfoRef,
    productFacilitiesInfoRef,
    productFirstProcessorInfoRef,
  ];

  // ===== UTILITY FUNCTIONS =====
  const resetFormData = () => {
    setStepData({
      productInfo: initTcProduct,
      geographicInfo: [],
      additionalInfo: initAdditonalInfo,
      rawMaterialInfo: [],
      productCertifiedWeightInfo: [],
      productFacilitiesInfo: [],
      productFirstProcessorInfo: [],
    });
    setProductId(null);
    setShipmentId(null);
    setShipmentNo(null);
    setOriginalProductArticleNo("");
    setOriginalShipmentIds([]);
    setPendingSave(null);
    setEditingIndex(-1);
    setIsEdit(false);
    setIsCopy(false);
  };

  // ===== DATA HANDLERS =====
  useEffect(() => {
    const keyMap: Record<number, keyof ProductFormData> = {
      1: "rawMaterialInfo",
      2: "geographicInfo",
      3: "productCertifiedWeightInfo",
      4: "productFacilitiesInfo",
      5: "productFirstProcessorInfo",
      6: "additionalInfo", //
    };

    const key = keyMap[activeStep];
    if (key) {
      updateField(key, stepData[key]);
    }
  }, [activeStep]);

  //save api
  const handleSave = async (payload: any) => {
    try {
      setLoadingState(true);

      // ========================================
      // EXTRACT CURRENT VALUES FROM PAYLOAD
      // ========================================

      const productInfo = payload.tcProductInformation?.[0];
      if (!productInfo) {
        notify("Invalid product information", "error");
        setLoadingState(false);
        return false;
      }

      const currentArticleNo = productInfo.tcProductArticleNo || "";
      const currentShipmentNos = Array.isArray(productInfo.tcProductShipmentDocNo)
        ? productInfo.tcProductShipmentDocNo
        : [productInfo.tcProductShipmentDocNo || ""];

      // Validate article number
      if (!currentArticleNo) {
        notify("Product Article Number is required", "error");
        setLoadingState(false);
        return false;
      }

      // ========================================
      // MAP SHIPMENT NUMBERS TO IDs
      // ========================================

      const currentShipmentIds = currentShipmentNos
        .map((shipmentNo: string) => {
          const shipment = getShipmentIdsByNumbers(shipmentData, shipmentNo);
          return shipment?.shipmentId;
        })
        .filter((id: string | undefined): id is string => !!id);

      // Validate shipment IDs
      if (currentShipmentIds.length === 0) {
        notify(
          `No valid shipment IDs found for shipment numbers: ${currentShipmentNos.join(
            ", "
          )}`,
          "error"
        );
        setLoadingState(false);
        return false;
      }

      // ========================================
      // SCENARIO DETECTION LOGIC
      // ========================================

      const isNewProduct = !productId || productId === "";
      const isCopyOperation = isCopy;

      // Compare shipment IDs (arrays must be sorted for comparison)
      const currentShipmentIdsSorted = [...currentShipmentIds].sort();
      const originalShipmentIdsSorted = [...originalShipmentIds].sort();
      const shipmentChanged =
        originalShipmentIds.length > 0 &&
        JSON.stringify(currentShipmentIdsSorted) !==
        JSON.stringify(originalShipmentIdsSorted);

      // Check if article number changed
      const articleNoChanged =
        originalProductArticleNo &&
        originalProductArticleNo !== currentArticleNo;

      let oldShipmentIds: string[];
      let oldArticleNo: string;
      let scenario = "";

      // ========================================
      // DETERMINE SCENARIO
      // ========================================

      if (isNewProduct || isCopyOperation) {
        // SCENARIO 1: New Product + New Shipment;
        oldShipmentIds = currentShipmentIds;
        oldArticleNo = currentArticleNo;
      } else if (shipmentChanged && !articleNoChanged) {
        // SCENARIO 2: Old Product - Edit - Change Associated Shipment Docs
        oldShipmentIds = originalShipmentIds;
        oldArticleNo = originalProductArticleNo || currentArticleNo;
      } else if (!shipmentChanged && articleNoChanged) {
        // SCENARIO 3: Old Product - Edit - Change Product Article No
        oldShipmentIds =
          originalShipmentIds.length > 0
            ? originalShipmentIds
            : currentShipmentIds;
        oldArticleNo = originalProductArticleNo;
      } else if (shipmentChanged && articleNoChanged) {
        // SCENARIO 4: Old Product - Edit - Change Both Shipment and Article No
        oldShipmentIds = originalShipmentIds;
        oldArticleNo = originalProductArticleNo;
      } else {
        // FALLBACK: Edit without changes (same as Scenario 2/3 logic)
        oldShipmentIds =
          originalShipmentIds.length > 0
            ? originalShipmentIds
            : currentShipmentIds;
        oldArticleNo = originalProductArticleNo || currentArticleNo;
      }

      // ========================================
      // BUILD QUERY PARAMETERS
      // ========================================

      const queryParams = new URLSearchParams({
        shipmentId: JSON.stringify(currentShipmentIds), // Current/new shipment IDs
        oldShipmentId: JSON.stringify(oldShipmentIds), // Original shipment IDs
        oldProductArticleNo: oldArticleNo, // Original article number
      });

      const apiUrl = `${TCAppConfig.POST_PRODUCT_INFO_V4
        }?${queryParams.toString()}`;

      // ========================================
      // BUILD CLEAN PAYLOAD
      // ========================================

      const cleanPayload = {
        tcId: TE_ID,
        ...(!isCopyOperation && productId ? { productId } : {}),
        tcProductInformation: [
          {
            tcProductOrderNo: Array.isArray(productInfo.tcProductOrderNo)
              ? productInfo.tcProductOrderNo
              : [productInfo.tcProductOrderNo || ""],
            tcProductArticleNo: productInfo.tcProductArticleNo || "",
            tcProductProductionDate: productInfo.tcProductProductionDate || "",
            tcProductShipmentDocNo: Array.isArray(productInfo.tcProductShipmentDocNo)
              ? productInfo.tcProductShipmentDocNo
              : [productInfo.tcProductShipmentDocNo || ""],
            tcProductNumberOfUnits:
              productInfo.tcProductNumberOfUnits?.toString() || "0",
            tcProductUnitsMetric: productInfo.tcProductUnitsMetric || "",
            tcProductNetShippingWeight:
              parseFloat(
                productInfo.tcProductNetShippingWeight?.toString() || "0"
              ) || 0,
            tcProductSupplementaryWeight:
              parseFloat(
                productInfo.tcProductSupplementaryWeight?.toString() || "0"
              ) || 0,
            tcProductInfoSupplementaryWeight:
              productInfo.tcProductInfoSupplementaryWeight || "",
            tcProductCategoryCode: productInfo.tcProductCategoryCode || "",
            tcProductCategoryUserSpecificTerm:
              productInfo.tcProductCategoryUserSpecificTerm || "",
            tcProductDetailCode: productInfo.tcProductDetailCode || "",
            tcProductDetailUserSpecificTerm:
              productInfo.tcProductDetailUserSpecificTerm || "",
            tcProductCategoryCodeDescription:
              productInfo.tcProductCategoryCodeDescription || "",
            tcProductDetailCodeDescription:
              productInfo.tcProductDetailCodeDescription || "",
            tcProductRawMaterialInformation: (
              productInfo.tcProductRawMaterialInformation || []
            ).map((item: any) => {
              const pctRaw =
                parseFloat(
                  item.tcProductRawMaterialPercentage?.toString() || "0"
                ) || 0;
              const pctRounded = Number(pctRaw.toFixed(2));
              return {
                tcProductRawMaterialCode: item.tcProductRawMaterialCode || "",
                tcProductRawMaterialPercentage: pctRounded,
                tcProductRawMaterialUserSpecificTerm:
                  item.tcProductRawMaterialUserSpecificTerm || "",
                tcProductRawMaterialCodeDescription:
                  item.tcProductRawMaterialCodeDescription || "",
              };
            }),
            tcProductCertifiedRawMaterialGeographicInformation: (
              productInfo.tcProductCertifiedRawMaterialGeographicInformation ||
              []
            ).map((item: any) => ({
              tcProductCertifiedRawMaterialCode:
                item.tcProductCertifiedRawMaterialCode || "",
              tcProductCertifiedRawMaterialCodeDescription:
                item.tcProductCertifiedRawMaterialCodeDescription || "",
              tcProductCertifiedRawMaterialWeight:
                parseFloat(
                  item.tcProductCertifiedRawMaterialWeight?.toString() || "0"
                ) || 0,
              tcProductCertifiedRawMaterialCountryOrArea:
                item.tcProductCertifiedRawMaterialCountryOrArea || "",
              tcProductCertifiedRawMaterialCountryOrAreaDescription:
                item.tcProductCertifiedRawMaterialCountryOrAreaDescription ||
                "",
              tcProductCertifiedRawMaterialStateOrProvince:
                item.tcProductCertifiedRawMaterialStateOrProvince || "",
              tcProductCertifiedRawMaterialStateOrProvinceDescription:
                item.tcProductCertifiedRawMaterialStateOrProvinceDescription ||
                "",
            })),
            tcProductCertifiedWeightInformation: (
              productInfo.tcProductCertifiedWeightInformation || []
            ).map((item: any) => ({
              tcProductClaimsCategory: item.tcProductClaimsCategory || "",
              tcProductCertifiedWeight:
                parseFloat(item.tcProductCertifiedWeight?.toString() || "0") ||
                0,
              tcProductRawMaterialCodeClaimsCategory:
                item.tcProductRawMaterialCodeClaimsCategory || "",
              tcProductRawMaterialCodeClaimsCategoryDescription:
                item.tcProductRawMaterialCodeClaimsCategoryDescription || "",
            })),
            tcProductFacilitiesInformation: (
              productInfo.tcProductFacilitiesInformation || []
            ).map((item: any) => ({
              tcProductFacilitiesTeId: item.tcProductFacilitiesTeId || "",
              tcProductFacilitiesProcessCode:
                item.tcProductFacilitiesProcessCode || "",
            })),
            tcProductFirstProcessorInformation: (
              productInfo.tcProductFirstProcessorInformation || []
            ).map((item: any) => ({
              tcProductFirstProcessorTeId:
                item.tcProductFirstProcessorTeId || "",
              tcProductFirstProcessCode: item.tcProductFirstProcessCode || "",
            })),
            tcProductAdditives: productInfo.tcProductAdditives || "",
            tcProductAdditivesWeight:
              parseFloat(
                productInfo.tcProductAdditivesWeight?.toString() || "0"
              ) || 0,
            tcProductInfoFiberLength:
              productInfo.tcProductInfoFiberLength || "",
            tcProductInfoYarnCount: productInfo.tcProductInfoYarnCount || "",
            tcProductInfoFabricWeightAndConstruct:
              productInfo.tcProductInfoFabricWeightAndConstruct || "",
            tcProductInfoFabricWeightOfFinishedProduct:
              productInfo.tcProductInfoFabricWeightOfFinishedProduct || "",
            tcProductInfoTechnicalSpecifications:
              productInfo.tcProductInfoTechnicalSpecifications || "",
            tcProductFeedstock: productInfo.tcProductFeedstock || "",
            tcProductPercentTextileWaste:
              productInfo.tcProductPercentTextileWaste || "",
            tcProductPackagingFor: normalizeStringArray(productInfo.tcProductPackagingFor),
            tcProductGtin: productInfo.tcProductGtin || "",
            tcProductReclaimedMaterialSource: Array.isArray(
              productInfo.tcProductReclaimedMaterialSource
            )
              ? productInfo.tcProductReclaimedMaterialSource.map(
                (item: string) => {
                  const raw = String(item).trim().toUpperCase();
                  const prPattern = /^PR\d{4}$/;
                  const digitsPattern = /^\d{4}$/;
                  if (digitsPattern.test(raw)) {
                    return `PR${raw}`;
                  } else if (prPattern.test(raw)) {
                    return raw;
                  } else {
                    return raw;
                  }
                }
              )
              : Array.isArray(productInfo.tcProductReclaimedMaterialSource)
                ? productInfo.tcProductReclaimedMaterialSource
                : [],
            tcProductInfoAdditional: productInfo.tcProductInfoAdditional || "",
            tcProductOriginFacilityTeId: Array.isArray(
              productInfo.tcProductOriginFacilityTeId
            )
              ? productInfo.tcProductOriginFacilityTeId
              : productInfo.tcProductOriginFacilityTeId
                ? [productInfo.tcProductOriginFacilityTeId]
                : [],
          },
        ],
      };

      // ========================================
      // EXECUTE API CALL
      // ========================================

      const result: any = await executePost(apiUrl, cleanPayload);

      if (result.status === 200) {
        notify("Product saved successfully", "success");

        // Clear pending save if exists
        if (pendingSave) {
          setPendingSave(null);
        }

        // Refresh product list
        resetFormData();
        toggleDrawer();
        await fetchProductDetails();

        await onNextTab();

        // Reset form and close drawer
        setIsCopy(false);

        setLoadingState(false);
        return true;
      } else if (result.status === 409) {
        setLoadingState(false);
        notify("Product already exists with this configuration", "warning");
        return false;
      } else {
        setLoadingState(false);
        notify(result?.message || "Unable to save product", "error");
        return false;
      }
    } catch (error: any) {
      console.error("Error saving product:", error);
      setLoadingState(false);
      notify(
        error?.message || "Failed to save product. Please try again.",
        "error"
      );
      return false;
    }
  };

  const handleDataUpdate = (
    stepKey: keyof ProductFormData,
    updatedData: any
  ) => {
    if (stepKey === "productInfo") {
      updatedData = {
        ...updatedData,
        tcProductNetShippingWeight:
          parseFloat(
            updatedData.tcProductNetShippingWeight?.toString() || "0"
          ) || 0,
        tcProductSupplementaryWeight:
          parseFloat(
            updatedData.tcProductSupplementaryWeight?.toString() || "0"
          ) || 0,
        tcProductNumberOfUnits:
          updatedData.tcProductNumberOfUnits?.toString() || "0",
        tcProductUnitsMetric: updatedData.tcProductUnitsMetric || "",
        tcProductOrderNo: Array.isArray(updatedData.tcProductOrderNo)
          ? updatedData.tcProductOrderNo
          : [updatedData.tcProductOrderNo || ""],
        tcProductShipmentDocNo: Array.isArray(updatedData.tcProductShipmentDocNo)
          ? updatedData.tcProductShipmentDocNo
          : [updatedData.tcProductShipmentDocNo || ""],
        tcProductArticleNo: updatedData.tcProductArticleNo || "",
        tcProductProductionDate: updatedData.tcProductProductionDate || "",
        tcProductInfoSupplementaryWeight:
          updatedData.tcProductInfoSupplementaryWeight || "",
        tcProductCategoryCode: updatedData.tcProductCategoryCode || "",
        tcProductCategoryUserSpecificTerm:
          updatedData.tcProductCategoryUserSpecificTerm || "",
        tcProductDetailCode: updatedData.tcProductDetailCode || "",
        tcProductDetailUserSpecificTerm:
          updatedData.tcProductDetailUserSpecificTerm || "",
        tcProductCategoryCodeDescription:
          updatedData.tcProductCategoryCodeDescription || "",
        tcProductDetailCodeDescription:
          updatedData.tcProductDetailCodeDescription || "",
      };
    } else if (stepKey === "rawMaterialInfo") {
      updatedData = updatedData.map(
        (item: ITcProductRawMaterialInformation) => ({
          ...item,
          tcProductRawMaterialPercentage:
            parseFloat(
              item.tcProductRawMaterialPercentage?.toString() || "0"
            ) || 0,
          tcProductRawMaterialCode: item.tcProductRawMaterialCode || "",
          tcProductRawMaterialUserSpecificTerm:
            item.tcProductRawMaterialUserSpecificTerm || "",
          tcProductRawMaterialCodeDescription:
            item.tcProductRawMaterialCodeDescription || "",
        })
      );
    } else if (stepKey === "geographicInfo") {
      updatedData = updatedData.map(
        (item: ITcProductRawMaterialGeographicInformation) => ({
          ...item,
          tcProductCertifiedRawMaterialWeight:
            parseFloat(
              item.tcProductCertifiedRawMaterialWeight?.toString() || "0"
            ) || 0,
          tcProductCertifiedRawMaterialCode:
            item.tcProductCertifiedRawMaterialCode || "",
          tcProductCertifiedRawMaterialCodeDescription:
            item.tcProductCertifiedRawMaterialCodeDescription || "",
          tcProductCertifiedRawMaterialCountryOrArea:
            item.tcProductCertifiedRawMaterialCountryOrArea || "",
          tcProductCertifiedRawMaterialCountryOrAreaDescription:
            item.tcProductCertifiedRawMaterialCountryOrAreaDescription || "",
          tcProductCertifiedRawMaterialStateOrProvince:
            item.tcProductCertifiedRawMaterialStateOrProvince || "",
          tcProductCertifiedRawMaterialStateOrProvinceDescription:
            item.tcProductCertifiedRawMaterialStateOrProvinceDescription || "",
        })
      );
    } else if (stepKey === "productCertifiedWeightInfo") {
      updatedData = updatedData.map(
        (item: ITcProductCertifiedWeightInformation) => ({
          ...item,
          tcProductCertifiedWeight:
            parseFloat(item.tcProductCertifiedWeight?.toString() || "0") || 0,
          tcProductClaimsCategory: item.tcProductClaimsCategory || "",
          tcProductRawMaterialCodeClaimsCategory:
            item.tcProductRawMaterialCodeClaimsCategory || "",
          tcProductRawMaterialCodeClaimsCategoryDescription:
            item.tcProductRawMaterialCodeClaimsCategoryDescription || "",
        })
      );
    } else if (stepKey === "additionalInfo") {
      const { tcProductOriginFacilityTeIdSearch, ...additionalInfoValues } =
        updatedData;
      updatedData = {
        ...additionalInfoValues,
        tcProductAdditivesWeight:
          parseFloat(updatedData.tcProductAdditivesWeight?.toString() || "0") ||
          0,
        tcProductAdditives: updatedData.tcProductAdditives || "",
        tcProductInfoFiberLength: updatedData.tcProductInfoFiberLength || "",
        tcProductInfoYarnCount: updatedData.tcProductInfoYarnCount || "",
        tcProductInfoFabricWeightAndConstruct:
          updatedData.tcProductInfoFabricWeightAndConstruct || "",
        tcProductInfoFabricWeightOfFinishedProduct:
          updatedData.tcProductInfoFabricWeightOfFinishedProduct || "",
        tcProductInfoTechnicalSpecifications:
          updatedData.tcProductInfoTechnicalSpecifications || "",
        tcProductFeedstock: updatedData.tcProductFeedstock || "",
        tcProductPercentTextileWaste:
          updatedData.tcProductPercentTextileWaste || "",
        tcProductPackagingFor: normalizeStringArray(updatedData.tcProductPackagingFor),
        tcProductGtin: updatedData.tcProductGtin || "",
        tcProductReclaimedMaterialSource:
          updatedData.tcProductReclaimedMaterialSource || "",
        tcProductInfoAdditional: updatedData.tcProductInfoAdditional || "",
        tcProductOriginFacilityTeId: Array.isArray(
          updatedData.tcProductOriginFacilityTeId
        )
          ? updatedData.tcProductOriginFacilityTeId
          : updatedData.tcProductOriginFacilityTeId
            ? [String(updatedData.tcProductOriginFacilityTeId)]
            : [],
      };
    } else if (stepKey === "productFacilitiesInfo") {
      updatedData = updatedData.map(
        (item: ITcProductFacilitiesInformation) => ({
          ...item,
          tcProductFacilitiesTeId: item.tcProductFacilitiesTeId || "",
          tcProductFacilitiesProcessCode:
            item.tcProductFacilitiesProcessCode || "",
        })
      );
    } else if (stepKey === "productFirstProcessorInfo") {
      updatedData = updatedData.map(
        (item: ITcProductFirstProcessorInformation) => ({
          ...item,
          tcProductFirstProcessorTeId: item.tcProductFirstProcessorTeId || "",
          tcProductFirstProcessCode: item.tcProductFirstProcessCode || "",
        })
      );
    }
    setStepData((prev) => ({
      ...prev,
      [stepKey]: updatedData,
    }));
    updateField(stepKey, updatedData);
  };

  const fetchShipmentData = async () => {
    try {
      // Assuming TCAppConfig has been updated with a new key for the v4 endpoint, e.g., TCAppConfig.GET_SHIPMENT_CONSIGNEE_DETAILS_V4
      // Replace with the actual config key/path as per your app config
      const response = await executeShipment(
        TCAppConfig.GET_SHIPMENTS_ENDPOINT_V4,
        {
          tcId: TE_ID,
        }
      );
      const shipments = response?.data.response || [];
      // Fetched shipment data
      setShipmentData(shipments);
    } catch (err) {
      console.error("Failed to fetch shipment data:", err);
      notify("Failed to fetch shipment data", "error");
    }
  };

  const fetchProductDetails = async () => {
    try {
      setIsInitialLoading(true);
      const offset = pagination.pageIndex * pagination.pageSize;
      const response: any = await executeGet(
        TCAppConfig.GET_LIST_PRODUCT_INFO_V4,
        { tcId: TE_ID, limit: pagination.pageSize, offset }
      );

      let result = response.data;
      let productCategories = await getProductCategory();

      // Handle response - it might be a string, array, or undefined
      let responseArray: any[] = [];

      if (result?.response) {
        // If response is a string, parse it
        if (typeof result.response === "string") {
          try {
            responseArray = JSON.parse(result.response);
          } catch (parseError) {
            console.error("Failed to parse response string:", parseError);
            responseArray = [];
          }
        }
        // If response is already an array, use it
        else if (Array.isArray(result.response)) {
          responseArray = result.response;
        }
        // Otherwise, default to empty array
        else {
          responseArray = [];
        }
      }

      // Only map if there are items in the array
      const mappedResponse =
        responseArray.length > 0
          ? responseArray.map((res: any) => {
            return {
              ...res,
              tcNetShippingWeight: res.tcProductNetShippingWeight,
              tcProductCategoryCodeDescription:
                productCategories.find(
                  (option: any) => option.value === res.tcProductCategoryCode
                )?.label || "",
              tcProductShipmentDocNo: Array.isArray(res.tcProductShipmentDocNo)
                ? res.tcProductShipmentDocNo
                : [res.tcProductShipmentDocNo],
            };
          })
          : [];

      setTableData(mappedResponse);
      setRowCount(result?.no_of_records || 0);
      setAPIData(mappedResponse);
      setDeleteSave(false);
      setNextEnabled(true);
    } catch (err) {
      console.error("Failed to fetch product details:", err);
      notify("Failed to fetch product details", "error");
    } finally {
      setIsInitialLoading(false);
    }
  };

  useEffect(() => {
    if (TE_ID) {
      fetchProductDetails();
    }
  }, [TE_ID, pagination?.pageIndex]);

  const getProductCategory = async () => {
    try {
      const result: any = await dtrackItService.getProductCategory();
      const productDetails = result.data.map((item: any) => ({
        value: item.code_1,
        label: `${item.description_1} (${item.code_1})`,
      }));
      return productDetails;
    } catch (err) {
      console.error("Error fetching product categories:", err);
      return [];
    }
  };

  const tableSteps = [1, 2, 3, 4, 5];

  // ===== STEP NAVIGATION =====
  const getStepData = (): ProductFormData =>
    getCurrentFormValues() as ProductFormData;

  // Validate conditional requirements for Product First Processor (uses cached standards - synchronous)
  const validateFirstProcessorRequirements = (): boolean => {
    try {
      if (tcStandardsCache.length === 0) return true; // if no standards cached, allow passage

      const standards = tcStandardsCache;
      const hasGRSorRCS =
        standards.includes("GRS") || standards.includes("RCS");

      // Facility process codes from step data
      const facilityCodes: string[] = Array.isArray(
        stepData.productFacilitiesInfo
      )
        ? stepData.productFacilitiesInfo
          .map((f: any) => String(f.tcProductFacilitiesProcessCode || ""))
          .filter(Boolean)
        : [];

      const triggerSet = new Set([
        "PR0043",
        "PR0003",
        "PR0004",
        "PR0042",
        "PR0005",
        "PR0006",
        "PR0017",
        "PR0044",
        "PR0024",
        "PR0047",
      ]);

      const facilityTriggers = facilityCodes.some((code) =>
        triggerSet.has(String(code))
      );

      // Mandatory ONLY when BOTH conditions are met: GRS/RCS AND trigger PR codes
      const isMandatory = hasGRSorRCS && facilityTriggers;

      if (!isMandatory) return true; // nothing to enforce

      // Now check first-processor entries
      const fp = stepData.productFirstProcessorInfo || [];
      if (!Array.isArray(fp) || fp.length === 0) {
        notify(
          "Atleast one process code is required for GRS/RCS transactions with selected process codes",
          "error"
        );
        return false;
      }

      // Ensure each entry has both fields populated
      for (const entry of fp) {
        const teId = entry?.tcProductFirstProcessorTeId;
        const procCode = entry?.tcProductFirstProcessCode;
        if (!teId || String(teId).trim() === "") {
          notify(
            "Product First Processor TE-ID is required for the selected standard/process codes.",
            "error"
          );
          return false;
        }
        if (!procCode || String(procCode).trim() === "") {
          notify(
            "Product First Process Code is required for the selected standard/process codes.",
            "error"
          );
          return false;
        }
      }

      return true;
    } catch (err) {
      console.error("Error validating first-processor requirements:", err);
      notify(
        "Unable to validate first-processor requirements. Please try again.",
        "error"
      );
      return false;
    }
  };
  const handleNext = async () => {
    try {
      const currentFormRef = formRefs[activeStep]?.current;
      const currentStepData = getStepData();

      // Table steps (1, 2, 3, 4, 5)
      if (tableSteps.includes(activeStep)) {
        const refsWithUnsaved = [
          {
            ref: rawMaterialInfoRef,
            data: currentStepData.rawMaterialInfo,
            step: 1,
          },
          {
            ref: geographicInfoRef,
            data: currentStepData.geographicInfo,
            step: 2,
          },
          {
            ref: productCertifiedWeightInfoRef,
            data: currentStepData.productCertifiedWeightInfo,
            step: 3,
          },
          {
            ref: productFacilitiesInfoRef,
            data: currentStepData.productFacilitiesInfo,
            step: 4,
          },
          {
            ref: productFirstProcessorInfoRef,
            data: currentStepData.productFirstProcessorInfo,
            step: 5,
          },
        ];
        const currentRefData = refsWithUnsaved.find(
          (r) => r.step === activeStep
        );

        if (currentRefData) {
          const formRef = currentRefData.ref.current;
          const isEditing = formRef?.editingRowIndex !== -1;
          const hasUnsaved = formRef?.hasUnsavedRow || false;

          if (isEditing) {
            notify(
              "Please save or cancel the current row before proceeding",
              "error"
            );
            return;
          }

          if (hasUnsaved) {
            notify(
              "Please complete all entries and ensure totals match before proceeding",
              "error"
            );
            return;
          }

          if (
            activeStep !== 5 &&
            (!currentRefData.data || currentRefData.data.length === 0)
          ) {
            notify(
              `At least one ${steps[activeStep].label} entry is required`,
              "error"
            );
            return;
          }

          if (activeStep === 1) {
            // Validate raw material percentages
            const rawMaterialData = currentStepData.rawMaterialInfo;
            const total = rawMaterialData.reduce((sum, item) => {
              const pct = Number(item.tcProductRawMaterialPercentage) || 0;
              if (item.tcProductRawMaterialCode || pct > 0) {
                return sum + pct;
              }
              return sum;
            }, 0 as number);

            // Check if total exceeds 100%
            if (total > 100) {
              notify(
                "Raw material percentages cannot exceed 100%. Current total: " +
                Number(total || 0).toFixed(2) +
                "%",
                "error"
              );
              return;
            }

            // Check if there are any raw materials entered but total is less than 100%
            const hasRawMaterialsEntered = rawMaterialData.some(
              (item) =>
                item.tcProductRawMaterialCode ||
                (item.tcProductRawMaterialPercentage ?? 0) > 0
            );

            if (hasRawMaterialsEntered && total < 100) {
              notify(
                "Raw material percentages must total 100%. Current total: " +
                Number(total || 0).toFixed(2) +
                "%",
                "error"
              );
              return;
            }
          }

          // Step 2: Geographic Info - Validate sum of certified weights matches total
          if (activeStep === 2) {
            const geoData = currentStepData.geographicInfo;
            const rawData = currentStepData.rawMaterialInfo;

            // Group geographic weights by raw material code
            const geosByCode = new Map<string, number>();
            geoData.forEach((geo: any) => {
              const code = geo.tcProductCertifiedRawMaterialCode;
              const weight =
                Number(geo.tcProductCertifiedRawMaterialWeight) || 0;
              geosByCode.set(code, (geosByCode.get(code) || 0) + weight);
            });

            // Helper function to check if raw material is certified
            const isCertifiedRM = (code: string): boolean => {
              if (!code || !certifiedRawMaterialCodes.length || !tcStandardsCache.length) return false;
              const mapping = certifiedRawMaterialCodes.find((item: any) => item.material_code === code);
              const rawStandards: string = mapping?.standard || "";
              if (!rawStandards || rawStandards.toUpperCase() === "NA") return false;
              const standards = rawStandards
                .split("/")
                .map((s: string) => s.trim())
                .filter((s: string) => s && s.toUpperCase() !== "NA");
              if (!standards.length) return false;
              const tcStandardsLower = tcStandardsCache.map((s) => String(s).toLowerCase());
              return standards.some((std) => tcStandardsLower.includes(std.toLowerCase()));
            };

            // Calculate expected weights and validate - ONLY for certified raw materials
            const netWeight = currentStepData.productInfo.tcProductNetShippingWeight || 0;
            const suppWeight = currentStepData.productInfo.tcProductSupplementaryWeight || 0;

            for (const raw of rawData) {
              const code = raw.tcProductRawMaterialCode;
              
              // Skip validation for non-certified raw materials
              if (!isCertifiedRM(code)) {
                console.debug("[CertifiedProducts] Skipping validation for non-certified RM:", code);
                continue;
              }
              
              const percentage = Number(raw.tcProductRawMaterialPercentage) || 0;
              const expectedWeight = (netWeight - suppWeight) * (percentage / 100);
              const actualWeight = geosByCode.get(code) || 0;

              if (Math.abs(actualWeight - expectedWeight) > 0.01) {
                notify(
                  `Geographic weight mismatch for ${raw.tcProductRawMaterialCodeDescription || code}. ` +
                  `Expected: ${expectedWeight.toFixed(2)} kg, Actual: ${actualWeight.toFixed(2)} kg`,
                  "error"
                );
                return;
              }
            }
          }

          if (activeStep === 3) {
            const certRef = productCertifiedWeightInfoRef.current;
            if (certRef) {
              const tableSum = certRef.tableSum || 0;
              const expectedTotal = certRef.expectedTotal || 0;
              if (Math.abs(tableSum - expectedTotal) > 0.01) {
                notify(
                  `Sum of Certified Weights (${tableSum.toFixed(
                    2
                  )} kg) must match Total Certified Product Weight (${expectedTotal.toFixed(
                    2
                  )} kg)`,
                  "error"
                );
                return;
              }
            }
          }

          // Validate Product First Processor Info requirements when leaving step 5
          if (activeStep === 5) {
            if (!validateFirstProcessorRequirements()) {
              return; // Validation failed, stay on step 5
            }
          }
        }

        // All validations for table steps passed, move to next step
        setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
        setRestrictPopUpSave(false);
        return;
      }

      // Form-based steps (0 and 6)
      if (
        (activeStep === 0 || activeStep === 6) &&
        currentFormRef &&
        typeof currentFormRef.validateForm === "function"
      ) {
        const errors = await currentFormRef.validateForm();
        Object.keys(errors).forEach((fieldName) => {
          currentFormRef.setFieldTouched(fieldName, true, false);
        });
        if (Object.keys(errors).length === 0) {
          if (typeof currentFormRef.submitForm === "function") {
            await currentFormRef.submitForm();
            const currentValues = currentFormRef.values;
            const stepKeyMap: Record<number, keyof ProductFormData> = {
              0: "productInfo",
              6: "additionalInfo",
            };
            const key = stepKeyMap[activeStep];
            if (key) {
              updateField(key, currentValues);
              setStepData((prev) => ({ ...prev, [key]: currentValues }));
            }
          }
          setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
        } else {
          notify("Please fill in the required values", "error");
        }
        setRestrictPopUpSave(false);
      }
    } catch (err) {
      console.error("Error in handleNext:", err);
      notify("An unexpected error occurred. Please try again.", "error");
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
    setRestrictPopUpSave(false);
  };

  const handleFinish = async () => {
    try {
      // Validate TE_ID first
      if (!TE_ID) {
        notify("TC ID is missing", "error");
        return;
      }

      // Validate first-processor conditional requirements before final save
      const fpOk = validateFirstProcessorRequirements();
      if (!fpOk) return;

      let updatedStepData = { ...stepData };

      // Validate the last step
      if (additionalInfoRef.current) {
        const errors = await additionalInfoRef.current.validateForm();
        Object.keys(errors).forEach((fieldName) => {
          additionalInfoRef.current.setFieldTouched(fieldName, true, false);
        });
        if (Object.keys(errors).length > 0) {
          Object.values(errors).forEach((error) => {
            if (error && typeof error === "string" && error.trim() !== "") {
              notify(error, "error");
            }
          });
          return;
        }

        await additionalInfoRef.current.submitForm();
        const finalValues = additionalInfoRef.current.values;

        const { tcProductOriginFacilityTeIdSearch, ...additionalValues } =
          finalValues;

        const parsedAdditional = {
          ...additionalValues,
          tcProductAdditivesWeight:
            parseFloat(
              additionalValues.tcProductAdditivesWeight?.toString() || "0"
            ) || 0,
          tcProductAdditives: additionalValues.tcProductAdditives || "",
          tcProductInfoFiberLength:
            additionalValues.tcProductInfoFiberLength || "",
          tcProductInfoYarnCount: additionalValues.tcProductInfoYarnCount || "",
          tcProductInfoFabricWeightAndConstruct:
            additionalValues.tcProductInfoFabricWeightAndConstruct || "",
          tcProductInfoFabricWeightOfFinishedProduct:
            additionalValues.tcProductInfoFabricWeightOfFinishedProduct || "",
          tcProductInfoTechnicalSpecifications:
            additionalValues.tcProductInfoTechnicalSpecifications || "",
          tcProductFeedstock: additionalValues.tcProductFeedstock || "",
          tcProductPercentTextileWaste:
            additionalValues.tcProductPercentTextileWaste || "",
          tcProductPackagingFor: normalizeStringArray(additionalValues.tcProductPackagingFor),
          tcProductGtin: additionalValues.tcProductGtin || "",
          tcProductReclaimedMaterialSource:
            additionalValues.tcProductReclaimedMaterialSource || "",
          tcProductInfoAdditional:
            additionalValues.tcProductInfoAdditional || "",
          tcProductOriginFacilityTeId: Array.isArray(
            additionalValues.tcProductOriginFacilityTeId
          )
            ? additionalValues.tcProductOriginFacilityTeId
            : additionalValues.tcProductOriginFacilityTeId
              ? [String(additionalValues.tcProductOriginFacilityTeId)]
              : [],
        };

        updatedStepData.additionalInfo = parsedAdditional;
        updateField("additionalInfo", parsedAdditional);
      }

      // Validate all required steps (step 5 is optional and handled by conditional validation)
      const requiredSteps = [
        {
          data: updatedStepData.rawMaterialInfo,
          name: "Raw Material Info",
          stepIndex: 1,
        },
        {
          data: updatedStepData.geographicInfo,
          name: "Geographic Info",
          stepIndex: 2,
        },
        {
          data: updatedStepData.productCertifiedWeightInfo,
          name: "Certified Weight Info",
          stepIndex: 3,
        },
        {
          data: updatedStepData.productFacilitiesInfo,
          name: "Facilities Info",
          stepIndex: 4,
        },
        // Step 5 (First Processor Info) is optional - conditional validation handles requirements
      ];

      for (const step of requiredSteps) {
        if (!step.data || step.data.length === 0) {
          setActiveStep(step.stepIndex);
          notify(`At least one entry is required for ${step.name}`, "error");
          return;
        }
      }

      // Validate Raw Material Info percentage for GOTS
      if (getTCStandardGOTS && updatedStepData.rawMaterialInfo) {
        const totalPercentage = updatedStepData.rawMaterialInfo.reduce(
          (sum, item) => {
            const percent = item.tcProductRawMaterialPercentage || 0;
            return sum + (isNaN(percent) ? 0 : percent);
          },
          0
        );
        if (Math.abs(totalPercentage - 100) > 0.01) {
          setActiveStep(1);
          notify(
            "Total Product Raw Material Percentage should be 100",
            "error"
          );
          return;
        }
      }

      // Use mapToPayload to create the payload
      const payload = mapToPayload(
        updatedStepData,
        TE_ID!,
        productId || undefined
      );

      // Call handleSave
      await handleSave(payload);
    } catch (err) {
      console.error("Error in handleFinish:", err);
      notify("An unexpected error occurred. Please try again.", "error");
    }
  };

  // ===== API HANDLERS =====
  // Complete handleSave for Product - Handles all 4 scenarios

  // ===== UI HANDLERS =====
  const toggleDrawer = () => {
    setActiveStep(0);
    if (drawerOpen) {
      resetFormData();
    } else {
      // Check product limit before opening drawer for new products
      if (!isEdit && rowCount >= MAX_PRODUCTS_ALLOWED) {
        notify(PRODUCT_LIMIT_ERROR, "error");
        return;
      }
    }
    setRestrictPopUpSave(false);
    setDrawerOpen((prev) => !prev);
  };

  const onAdd = () => {
    setIsEdit(false);
    resetFormData();
    toggleDrawer();
  };

  const onCopy = (row: any) => {
    if (rowCount >= MAX_PRODUCTS_ALLOWED) {
      notify(PRODUCT_LIMIT_ERROR, "error");
      return;
    }

    setIsCopy(true);
    setIsEdit(false);

    const value = row?.original as ProductShipmentResponse;

    setProductId(null);
    setShipmentId(null);

    setOriginalProductArticleNo("");
    setOriginalShipmentIds([]);

    setShipmentNo(
      value?.tcProductShipmentDocNo && value?.tcProductShipmentDocNo?.length > 0
        ? value?.tcProductShipmentDocNo[0]
        : null
    );

    if (TE_ID && value.productId) {
      fetchProductaddDetails(
        executeGet,
        TE_ID,
        value.productId,
        value.shipmentId
      )
        .then((data) => {
          const responseData = data?.data;
          const product =
            responseData?.tcProductInformation?.[0] || responseData;

          if (product) {
            const netShippingWeight = product.tcProductNetShippingWeight || 0;

            const copiedProduct = {
              ...product,
              productId: null,
              shipmentId: null,
              tcProductArticleNo: product.tcProductArticleNo || "",
              tcProductShipmentDocNo: Array.isArray(product.tcProductShipmentDocNo)
                ? product.tcProductShipmentDocNo
                : product.tcProductShipmentDocNo
                  ? [product.tcProductShipmentDocNo]
                  : [],
              tcProductOrderNo: Array.isArray(product.tcProductOrderNo)
                ? product.tcProductOrderNo.map((no: string) => `${no}`)
                : product.tcProductOrderNo
                  ? [`${product.tcProductOrderNo}`]
                  : [],
              tcProductCertifiedRawMaterialGeographicInformation: (
                product.tcProductCertifiedRawMaterialGeographicInformation || []
              ).map((geoItem: any) => ({
                tcProductCertifiedRawMaterialCode:
                  geoItem.tcProductCertifiedRawMaterialCode || "",
                tcProductCertifiedRawMaterialCodeDescription:
                  geoItem.tcProductCertifiedRawMaterialCodeDescription || "",
                tcProductCertifiedRawMaterialWeight:
                  parseFloat(
                    geoItem.tcProductCertifiedRawMaterialWeight?.toString() ||
                    "0"
                  ) || 0,
                tcProductCertifiedRawMaterialCountryOrArea:
                  geoItem.tcProductCertifiedRawMaterialCountryOrArea || "",
                tcProductCertifiedRawMaterialCountryOrAreaDescription:
                  geoItem.tcProductCertifiedRawMaterialCountryOrAreaDescription ||
                  "",
                tcProductCertifiedRawMaterialStateOrProvince:
                  geoItem.tcProductCertifiedRawMaterialStateOrProvince || "",
                tcProductCertifiedRawMaterialStateOrProvinceDescription:
                  geoItem.tcProductCertifiedRawMaterialStateOrProvinceDescription ||
                  "",
              })),
            };

            const mappedStepData = mapProductDataToStepData(
              copiedProduct,
              netShippingWeight
            );

            setStepData(mappedStepData);

            Object.entries(mappedStepData).forEach(([key, val]) => {
              updateField(key as keyof ProductFormData, val);
            });

            setOriginalProductArticleNo("");
            setOriginalShipmentIds([]);

            toggleDrawer();
          } else {
            notify("No product data found", "error");
          }
        })
        .catch((err) => {
          console.error("Error copying product:", err);
          notify(err.message || "Failed to copy product", "error");
        });
    }
  };

  const onEdit = (row: any) => {
    setIsEdit(true);
    const value = row?.original as ProductShipmentResponse;
    setProductId(value.productId);
    setShipmentId(value.shipmentId);
    setShipmentNo(
      value?.tcProductShipmentDocNo && value?.tcProductShipmentDocNo?.length > 0
        ? value?.tcProductShipmentDocNo[0]
        : null
    );
    if (TE_ID) {
      fetchProductaddDetails(
        executeGet,
        TE_ID,
        value.productId,
        value.shipmentId
      )
        .then((data) => {

          // Extract the product from the nested structure
          const responseData = data?.data;
          const product =
            responseData?.tcProductInformation?.[0] || responseData;

        

          if (product) {
            const netShippingWeight = product.tcProductNetShippingWeight || 0;

            // Create mapped step data
            const mappedStepData: ProductFormData = {
              productInfo: {
                tcProductOrderNo: Array.isArray(product.tcProductOrderNo)
                  ? product.tcProductOrderNo
                  : [product.tcProductOrderNo || ""],
                tcProductArticleNo: product.tcProductArticleNo || "",
                tcProductProductionDate: product.tcProductProductionDate || "",
                tcProductShipmentDocNo: Array.isArray(product.tcProductShipmentDocNo)
                  ? product.tcProductShipmentDocNo
                  : [product.tcProductShipmentDocNo || ""],
                tcProductNumberOfUnits:
                  product.tcProductNumberOfUnits?.toString() || "0",
                tcProductUnitsMetric: product.tcProductUnitsMetric || "",
                tcProductNetShippingWeight:
                  parseFloat(
                    product.tcProductNetShippingWeight?.toString() || "0"
                  ) || 0,
                tcProductSupplementaryWeight:
                  parseFloat(
                    product.tcProductSupplementaryWeight?.toString() || "0"
                  ) || 0,
                tcProductInfoSupplementaryWeight:
                  product.tcProductInfoSupplementaryWeight || "",
                tcProductCategoryCode: product.tcProductCategoryCode || "",
                tcProductCategoryUserSpecificTerm:
                  product.tcProductCategoryUserSpecificTerm || "",
                tcProductDetailCode: product.tcProductDetailCode || "",
                tcProductDetailUserSpecificTerm:
                  product.tcProductDetailUserSpecificTerm || "",
                tcProductCategoryCodeDescription:
                  product.tcProductCategoryCodeDescription || "",
                tcProductDetailCodeDescription:
                  product.tcProductDetailCodeDescription || "",
              },
              rawMaterialInfo: (
                product.tcProductRawMaterialInformation || []
              ).map((item: any) => ({
                tcProductRawMaterialCode: item.tcProductRawMaterialCode || "",
                tcProductRawMaterialPercentage:
                  parseFloat(
                    item.tcProductRawMaterialPercentage?.toString() || "0"
                  ) || 0,
                tcProductRawMaterialUserSpecificTerm:
                  item.tcProductRawMaterialUserSpecificTerm || "",
                tcProductRawMaterialCodeDescription:
                  item.tcProductRawMaterialCodeDescription || "",
              })),
              geographicInfo: (
                product.tcProductCertifiedRawMaterialGeographicInformation || []
              ).map((item: any) => ({
                tcProductCertifiedRawMaterialCode:
                  item.tcProductCertifiedRawMaterialCode || "",
                tcProductCertifiedRawMaterialCodeDescription:
                  item.tcProductCertifiedRawMaterialCodeDescription || "",
                tcProductCertifiedRawMaterialWeight:
                  parseFloat(
                    item.tcProductCertifiedRawMaterialWeight?.toString() || "0"
                  ) || 0,
                tcProductCertifiedRawMaterialCountryOrArea:
                  item.tcProductCertifiedRawMaterialCountryOrArea || "",
                tcProductCertifiedRawMaterialCountryOrAreaDescription:
                  item.tcProductCertifiedRawMaterialCountryOrAreaDescription ||
                  "",
                tcProductCertifiedRawMaterialStateOrProvince:
                  item.tcProductCertifiedRawMaterialStateOrProvince || "",
                tcProductCertifiedRawMaterialStateOrProvinceDescription:
                  item.tcProductCertifiedRawMaterialStateOrProvinceDescription ||
                  "",
              })),
              productCertifiedWeightInfo: (
                product.tcProductCertifiedWeightInformation || []
              ).map((item: any) => ({
                tcProductClaimsCategory: item.tcProductClaimsCategory || "",
                tcProductCertifiedWeight:
                  parseFloat(
                    item.tcProductCertifiedWeight?.toString() || "0"
                  ) || 0,
                tcProductRawMaterialCodeClaimsCategory:
                  item.tcProductRawMaterialCodeClaimsCategory || "",
                tcProductRawMaterialCodeClaimsCategoryDescription:
                  item.tcProductRawMaterialCodeClaimsCategoryDescription || "",
              })),
              productFacilitiesInfo: (
                product.tcProductFacilitiesInformation || []
              ).map((item: any) => ({
                tcProductFacilitiesTeId: item.tcProductFacilitiesTeId || "",
                tcProductFacilitiesProcessCode:
                  item.tcProductFacilitiesProcessCode || "",
              })),
              productFirstProcessorInfo: (
                product.tcProductFirstProcessorInformation || []
              ).map((item: any) => ({
                tcProductFirstProcessorTeId:
                  item.tcProductFirstProcessorTeId || "",
                tcProductFirstProcessCode: item.tcProductFirstProcessCode || "",
              })),
              additionalInfo: {
                tcProductInfoAdditional: product.tcProductInfoAdditional || "",
                tcProductInfoFabricWeightAndConstruct:
                  product.tcProductInfoFabricWeightAndConstruct || "",
                tcProductInfoFabricWeightOfFinishedProduct:
                  product.tcProductInfoFabricWeightOfFinishedProduct || "",
                tcProductInfoFiberLength:
                  product.tcProductInfoFiberLength || "",
                tcProductInfoYarnCount: product.tcProductInfoYarnCount || "",
                tcProductAdditives: product.tcProductAdditives || "",
                tcProductAdditivesWeight:
                  parseFloat(
                    product.tcProductAdditivesWeight?.toString() || "0"
                  ) || 0,
                tcProductFeedstock: product.tcProductFeedstock || "",
                tcProductPercentTextileWaste:
                  product.tcProductPercentTextileWaste || "",
                tcProductPackagingFor: normalizeStringArray(product.tcProductPackagingFor),
                tcProductGtin: product.tcProductGtin || "",
                tcProductReclaimedMaterialSource:
                  product.tcProductReclaimedMaterialSource || "",
                tcProductInfoTechnicalSpecifications:
                  product.tcProductInfoTechnicalSpecifications || "",
                tcProductOriginFacilityTeId: Array.isArray(
                  product.tcProductOriginFacilityTeId
                )
                  ? product.tcProductOriginFacilityTeId
                  : product.tcProductOriginFacilityTeId
                    ? [product.tcProductOriginFacilityTeId]
                    : [],
              },
            };

            

            setStepData(mappedStepData);

            // Update form fields
            Object.entries(mappedStepData).forEach(([key, value]) => {
              updateField(key as keyof ProductFormData, value);
            });

            // Set originals for change detection in scenarios
            setOriginalProductArticleNo(product.tcProductArticleNo || "");
            const shipmentNos = Array.isArray(product.tcProductShipmentDocNo)
              ? product.tcProductShipmentDocNo
              : [product.tcProductShipmentDocNo || ""];
            const shipmentIds = shipmentNos
              .map(
                (no: any) =>
                  getShipmentIdsByNumbers(shipmentData, no)?.shipmentId
              )
              .filter((id: any): id is string => !!id);
            setOriginalShipmentIds(shipmentIds);

            toggleDrawer();
          } else {
            notify("No product data found", "error");
          }
        })
        .catch((err) => {
          console.error("Error fetching product details:", err);
          notify(err.message || "Failed to load product details", "error");
        });
    }
  };

  const onDelete = (row: any) => {
    const productArticleNo = row?.original?.tcProductArticleNo;
    const productId = row?.original?.productId;
    const shipmentId = row?.original?.shipmentId;
    const shipmentNo = row?.original?.tcProductShipmentDocNo?.[0] ?? null;

    setProductId(productId);
    setShipmentId(shipmentId);
    setShipmentNo(shipmentNo);
    setTcProductArticleNo(productArticleNo);
    setDialogOpen(true);
    setPopUpBtnName("Delete");
    setPopUpTitle("Confirm Delete");
    setPopUpMessage(
      `Deleting Product ${productArticleNo} will remove all associated records. Are you sure you want to delete it?`
    );
  };

  const handleConfirmSave = async () => {
    try {
      if (!productId) {
        notify("No product selected for deletion", "error");
        return;
      }

      const res = await executeDelete(`${TCAppConfig.TC_DELETE_API_V4}`, {
        tcId: TE_ID,
        shipmentId: shipmentId,
        productId: productId,
        productArticleNo: tcProductArticleNo,
      });

      if (res?.status === 200) {
        setDialogOpen(false);
        resetFormData();
        await fetchProductDetails();
        notify("Successfully deleted", "success");
      }
    } catch (error) {
      console.error("Error in handleConfirmSave:", error);
      notify("Failed to delete the product", "error");
    }
  };

  const handleCancelSave = () => {
    setDialogOpen(false);
    if (drawerOpen) {
      toggleDrawer();
    }
  };

  // ===== TABLE CONFIGURATION =====
  const columns: MRT_ColumnDef<any>[] = [
    {
      accessorKey: "tcProductOrderNo",
      header: "Product Order Number",
      minSize: 150,
      maxSize: 200,
      Cell: ({ cell }) => {
        const value = cell.getValue() as string[] | string | undefined;
        if (Array.isArray(value)) {
          return <div>{value.join(", ")}</div>;
        }
        return <div>{value || ""}</div>;
      },
    },
    {
      accessorKey: "tcProductArticleNo",
      header: "Product Article Number",
      minSize: 150,
      maxSize: 200,
    },
    {
      accessorKey: "tcProductNumberOfUnits",
      header: "Number of Units",
      minSize: 60,
      maxSize: 150,
      Cell: Utills.usNumberFormat,
    },
    {
      accessorKey: "tcProductUnitsMetric",
      header: "Units Metric",
      minSize: 60,
      maxSize: 150,
    },
    {
      accessorKey: "tcNetShippingWeight",
      header: "Net Shipping Weight (kg)",
      minSize: 60,
      maxSize: 150,
      Cell: Utills.usNumberFormat,
    },
    {
      accessorKey: "tcProductSupplementaryWeight",
      header: "Supplementary Weight (kg)",
      minSize: 60,
      maxSize: 150,
      Cell: Utills.usNumberFormat,
    },
    {
      accessorKey: "tcProductProductionDate",
      header: "Production Date",
      minSize: 60,
      maxSize: 150,
    },
    {
      accessorKey: "tcProductCategoryCodeDescription",
      header: "Product Category",
      minSize: 150,
      maxSize: 200,
    },
    {
      accessorKey: "tcProductDetailCode",
      header: "Product Detail Code",
      minSize: 150,
      maxSize: 200,
    },
    {
      accessorKey: "tcProductShipmentDocNo",
      header: "TC Product Shipment Doc No",
      minSize: 60,
      Cell: ({ cell }) => {
        const selectedValues = (cell.getValue() as string[]) || [];
        return (
          <div>
            {Array.isArray(selectedValues) && selectedValues.length > 0
              ? selectedValues.join(", ")
              : ""}
          </div>
        );
      },
      maxSize: 150,
    },
  ];

  // ===== STEPPER CONFIGURATION =====
  const steps = [
    {
      label: "Product Information",
      content: (
        <ProductInfo
          formRef={productInfoRef}
          productData={stepData.productInfo}
          setProductData={(data) => handleDataUpdate("productInfo", data)}
          shipmentId={shipmentId || ""}
        />
      ),
    },
    {
      label: "Product Raw Material Info",
      content: (
        <RawMaterialInfo
          formRef={rawMaterialInfoRef}
          rawMaterialInfoData={stepData.rawMaterialInfo}
          setRawMaterialInfoData={(data) =>
            handleDataUpdate("rawMaterialInfo", data)
          }
          onValidationChange={(isValid) => setIsRawMaterialValid(isValid)}
        />
      ),
    },
    {
      label: "Product Raw Material Geographic Info",
      content: (
        <ProductGeoInfo
          formRef={geographicInfoRef}
          productGeoData={
            Array.isArray(stepData.geographicInfo)
              ? stepData.geographicInfo
              : []
          }
          setProductGeoData={(data) => handleDataUpdate("geographicInfo", data)}
          productData={stepData.productInfo}
          rawMaterialInfoData={stepData.rawMaterialInfo}
        />
      ),
    },
    {
      label: "Product Certified Weight Info",
      content: (
        <ProductCertifiedWeight
          formRef={productCertifiedWeightInfoRef}
          productCertifiedWeightInfoData={stepData.productCertifiedWeightInfo}
          setProductCertifiedWeightInfoData={(data: any) =>
            handleDataUpdate("productCertifiedWeightInfo", data)
          }
          productData={stepData.productInfo}
          rawMaterialInfoData={stepData.rawMaterialInfo}
        />
      ),
    },
    {
      label: "Product Facilities Info",
      content: (
        <ProductFacilitiesInfo
          formRef={productFacilitiesInfoRef}
          ProductFacilitiesData={stepData.productFacilitiesInfo}
          setProductFacilitiesInfoData={(data) =>
            handleDataUpdate("productFacilitiesInfo", data)
          }
        />
      ),
    },
    {
      label: "Product First Processor Info",
      content: (
        <ProductFirstProcessorInfo
          formRef={productFirstProcessorInfoRef}
          ProductFirstProcessData={stepData.productFirstProcessorInfo}
          setProductFirstProcessInfoData={(data) =>
            handleDataUpdate("productFirstProcessorInfo", data)
          }
          facilityProcessCodes={
            Array.isArray(stepData.productFacilitiesInfo)
              ? stepData.productFacilitiesInfo
                .map((f: any) =>
                  String(f.tcProductFacilitiesProcessCode || "")
                )
                .filter(Boolean)
              : []
          }
        />
      ),
    },
    {
      label: "Product Additional Info",
      content: (
        <AdditionalInfo
          formRef={additionalInfoRef}
          additionalInfoData={stepData.additionalInfo}
          setAdditionalInfoData={(data) =>
            handleDataUpdate("additionalInfo", data)
          }
          productCategoryCode={stepData.productInfo.tcProductCategoryCode}
        />
      ),
    },
  ];

  const actions: ActionButtonConfig[] = [
    ...(activeStep !== 0
      ? [
        {
          label: "Back",
          onClick: handleBack,
          variant: "contained" as "contained",
          color: "default" as "default",
          disabled: [1, 2, 3, 4, 5].includes(activeStep)
            ? isRestrictPopUpSave
            : false,
        },
      ]
      : []),
    {
      label: activeStep === steps.length - 1 ? "Save" : "Next",
      onClick: activeStep === steps.length - 1 ? handleFinish : handleNext,
      variant: "contained" as "contained",
      color: "primary" as "primary",
      disabled: [1, 2, 3, 4, 5].includes(activeStep)
        ? isRestrictPopUpSave
        : false,
      isLoading: nextLoader && activeStep === 0,
    },
  ];

  // ===== INITIALIZATION =====
  useEffect(() => {
    const initializeComponent = async () => {
      if (TE_ID) {
        // Pre-fetch TC standards and certified RM codes for validation caching (non-blocking)
        try {
          const [tcResp, rmResp] = await Promise.all([
            executeGet(TCAppConfig.GET_TC_BASE_DETAILS_V4, { tcId: TE_ID }),
            executeGet(TCAppConfig.GET_CERTIFIED_RAW_MATERIAL_CODES),
          ]);
          
          const tcData = tcResp?.data || {};
          const standards: string[] = Array.isArray(tcData?.tcStandard)
            ? tcData.tcStandard
            : [];
          setTcStandardsCache(standards);
          
          const rmList = rmResp?.data?.response || rmResp?.data || [];
          setCertifiedRawMaterialCodes(Array.isArray(rmList) ? rmList : []);
        } catch (err) {
          console.error("Failed to fetch TC standards or certified RM codes:", err);
        }

        await Promise.all([fetchProductDetails(), fetchShipmentData()]);
      } else {
        updateTableData([]);
      }
      resetFormValueByPage();
      setRestrictPopUpSave(false);
    };

    initializeComponent();
  }, [TE_ID]);

  // ===== NAVIGATION HANDLERS =====
  const onNext = async () => {
    navigate(`/v4/tc/${formtype}/component-details/${TE_ID}`);
  };

  const onBack = () => {
    navigate(`/v4/tc/${formtype}/shipment-details/${TE_ID}`);
  };

  // ===== RENDER =====
  if (loading || isInitialLoading) {
    return <LoaderComponent />;
  }

  return (
    <>
      {/* Add Product Button */}
      <Box display="flex" mb={3} justifyContent="flex-end" width="100%">
        {!hasSubmitTc && (
          <Tooltip
            title={rowCount >= MAX_PRODUCTS_ALLOWED ? PRODUCT_LIMIT_ERROR : ""}
          >
            <span>
              <StyledButton
                variant="contained"
                sx={{ boxShadow: "none" }}
                onClick={onAdd}
              // disabled={rowCount >= MAX_PRODUCTS_ALLOWED}
              >
                Add Product Information
              </StyledButton>
            </span>
          </Tooltip>
        )}
      </Box>

      {/* Products Table */}
      <Box display="grid">
        <CustomTable
          columns={columns}
          data={tableData}
          enableTopToolbar={false}
          enableBottomToolbar={true}
          enableRowActions={!hasSubmitTc}
          rowCount={rowCount}
          paginations={pagination}
          setPaginations={setPagination}
          renderDetailPanel={({ row }) =>
            row.getIsExpanded() ? (
              <RenderDetailPanel row={row} />
            ) : (
              <div>No data available</div>
            )
          }
          renderRowActions={({ row }) => (
            <ActionsMenu
              row={row}
              onEdit={onEdit}
              onCopy={onCopy}
              onDelete={onDelete}
            />
          )}
        />
      </Box>

      {/* Product Form Modal */}
      <ActionsModalPopup
        open={drawerOpen}
        onClose={toggleDrawer}
        title={isEdit ? "Edit Product" : "Add Product"}
        actions={actions}
      >
        <CustomStepper
          steps={steps}
          activeStep={activeStep}
          onStepChange={(step) => setActiveStep(step)}
        />
      </ActionsModalPopup>

      {/* Navigation Buttons */}
      {!drawerOpen ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            mt: 2,
            gap: "0.5rem",
          }}
        >
          {/* {!isNextEnabled && <InnerButton onClick={handleSave}>Save & Next</InnerButton>} */}
          <NavLink to="/v4/tc-dashboard">
            <InnerButton>Home</InnerButton>
          </NavLink>
          <NavLink to={`/v4/tc/${formtype}/shipment-details/${TE_ID}`}>
            <InnerButton sx={{ boxShadow: "none" }}>Back</InnerButton>
          </NavLink>
          {tableData?.length > 0 && (
            <InnerButton onClick={onNext}>Next</InnerButton>
          )}
        </Box>
      ) : (
        <>
          {hasSubmitTc && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mt: 2,
                gap: "0.5rem",
              }}
            >
              <NavLink to={`/v4/tc/${formtype}/shipment-details/${TE_ID}`}>
                <InnerButton sx={{ boxShadow: "none" }}>Back</InnerButton>
              </NavLink>
              <InnerButton onClick={onNext} sx={{ boxShadow: "none" }}>
                Next
              </InnerButton>
            </Box>
          )}
        </>
      )}

      {/* Confirmation Dialog */}
      <AlertPopup
        open={dialogOpen}
        title={popUpTitle}
        message={popUpMessage}
        onClose={handleCancelSave}
        onConfirm={handleConfirmSave}
        confirmText={popUpBtnName}
        cancelText="Cancel"
      />
    </>
  );
};

export default CertifiedProductsComponents;
